import { P2GBox } from '@parcel2go/core-ui';
import { P2GButton } from '@parcel2go/core-ui';
import { P2GCheckBoxLegacy } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { useEffect, useMemo, useState } from 'react';
import { ExtraBase, QuoteOptionBaseApiModel, RuleApiModel } from '~/lib/api/smartsend/types';
import { CoverExtra, ExtendedBaseCoverExtra } from '../../ExtrasHelper';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { GetDefaultRuleByName } from '../rules/RuleLocator';
import {
  generateDefaultExtrasRule,
  generateDefaultExtrasRuleActions,
  GetExtrasFromRule,
  RuleNames
} from '../rules/RuleGenerator';
import { P2GProgress } from '@parcel2go/core-ui';
import { ProtectionSelectionDisplay } from '../ProtectionSelectionDisplay';
import { generateProtectionKey } from '../generateProtectionKey';
import { EventSections } from '../../../lib/events/EventLogger';
import { P2GText } from '@parcel2go/core-ui';
import { GetExtrasFromSettings } from '~/lib/settings/settings-helper';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props {
  confirm?: (extras: ExtraBase[]) => void;
  cancel?: () => void;
  affected: number;
  selectedItemCount?: number;
  filterContext?: string;
  protectionIds: string[];
  showDefaultRule: boolean;
  getRules: (type: 'SmartSend') => Promise<RuleApiModel[]>;
  createRule: (rule: RuleApiModel) => Promise<boolean>;
  updateRule: (rule: RuleApiModel) => Promise<boolean>;
}

export default function Protection(props: props) {
  const { getRules, protectionIds, showDefaultRule } = props;
  const [loading, setLoading] = useState(props.showDefaultRule);
  const [extras, setExtras] = useState<ExtraBase[]>([]);
  const [rule, setRule] = useState<RuleApiModel>();
  const [save, setSave] = useState(false);
  const [protectionKey, setProtectionKey] = useState<string>(null);
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const options = useMemo(
    () => GetExtrasFromSettings(SettingsCache.settings).filter((x) => protectionIds.includes(x.Id)),
    [protectionIds]
  );

  const availableProtectionOptions = useMemo(() => {
    if (options.length === 0) {
      return [];
    }

    const output: QuoteOptionBaseApiModel[] = [];

    if (options.find((o) => o.Id == ExtendedBaseCoverExtra)) {
      output.push({ CoverCost: 0, Covered: 20, CoverPercentage: 50, Extras: [{ Id: ExtendedBaseCoverExtra } as any] });
    }

    if (options.find((o) => o.Id == CoverExtra)) {
      if (options.find((o) => o.Id == ExtendedBaseCoverExtra)) {
        output.push({
          CoverCost: 0,
          Covered: 100,
          CoverPercentage: 100,
          Extras: [{ Id: CoverExtra } as any, { Id: ExtendedBaseCoverExtra } as any]
        });
      } else {
        output.push({ CoverCost: 0, Covered: 100, CoverPercentage: 100, Extras: [{ Id: CoverExtra } as any] });
      }
    }

    output.forEach((x) => {
      x.Key = generateProtectionKey(x.Extras);
    });

    return output;
  }, [options]);

  // RuleManager Rules
  useEffect(() => {
    if (!availableProtectionOptions.length) {
      return;
    }
    if (showDefaultRule) {
      getRules('SmartSend')
        .then((r) => {
          const dRule = r.find((x) => GetDefaultRuleByName(x, RuleNames.Extras));
          if (dRule) {
            const ex = GetExtrasFromRule(dRule);
            setRule(dRule);
            setSave(true);
            setExtras(ex);
            setProtectionKey(generateProtectionKey(ex));
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setProtectionKey(availableProtectionOptions[0].Key);
    }
  }, [availableProtectionOptions, getRules, showDefaultRule]);

  useEffect(() => {
    if (protectionKey === null) {
      return;
    }

    setExtras((previousExtras) => {
      // Remove all protection extras from existing selection.
      const nextExtras = previousExtras.filter((x) => !protectionIds.includes(x.Id));

      // Add protection extras based on selected protection option.
      const option = availableProtectionOptions.find((x) => x.Key === protectionKey);
      option?.Extras.forEach((selection) => {
        nextExtras.push(selection);
      });

      const unchanged =
        previousExtras.length === nextExtras.length &&
        previousExtras.every((extra, index) => extra.Id === nextExtras[index]?.Id);

      return unchanged ? previousExtras : nextExtras;
    });
  }, [availableProtectionOptions, protectionIds, protectionKey]);

  const confirm = () => {
    const toAdd = [...extras];
    // RuleManager
    if (showDefaultRule && save) {
      if (rule) {
        props.updateRule({ ...rule, Actions: generateDefaultExtrasRuleActions(toAdd) });
      } else {
        track({
          type: 'inline_rules_added',
          payload: GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.InlineRules], {
            rule: 'default-protection'
          })
        });

        props.createRule(generateDefaultExtrasRule(toAdd));
      }
    }

    props.confirm(toAdd);
  };

  return (
    <P2GBox textAlign="left">
      {loading ? (
        <P2GBox>
          <P2GProgress />
        </P2GBox>
      ) : (
        <P2GGrid container direction="column" spacing={3}>
          <P2GGrid item>
            <ProtectionSelectionDisplay
              selectedKey={protectionKey}
              options={availableProtectionOptions}
              setSelected={setProtectionKey}
              parcelValue={null}
              currencyCode={null}
            />
          </P2GGrid>
          {props.showDefaultRule && (
            <P2GGrid item>
              <P2GCheckBoxLegacy
                checked={save}
                label={
                  <P2GText variant="body2">
                    {t('AUTO_APPLY_INPUT_LABEL', { defaultValue: 'Auto apply when importing new items' })}
                  </P2GText>
                }
                id="Save"
                onChange={setSave}
                labelPlacement="end"
                variant="primary"
                error={false}
              />
            </P2GGrid>
          )}
        </P2GGrid>
      )}
      <P2GBox mt={2}>
        <P2GGrid container spacing={2}>
          {props.confirm && (
            <P2GGrid item>
              <P2GButton disabled={loading} onClick={confirm}>
                {t('SAVE_BUTTON', { defaultValue: 'Save' })}
              </P2GButton>
            </P2GGrid>
          )}
          {props.cancel && (
            <P2GGrid item>
              <P2GButton variant="primary" onClick={props.cancel}>
                {t('CANCEL_BUTTON', { defaultValue: 'Cancel' })}
              </P2GButton>
            </P2GGrid>
          )}
        </P2GGrid>
      </P2GBox>
    </P2GBox>
  );
}
